/**
 * Shared hospital master list UI (search, table, pagination, CSV, CRUD).
 * Used by HospitalPage (URL sync) and HospitalSearchModal (local URLSearchParams state).
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import {
  useMainHospitalSearch,
  useMainHospitalDetail,
  useMainHospitalCreate,
  useMainHospitalUpdate,
  useMainHospitalDelete,
  useMainHospitalExportCsv,
} from '@/hooks/main/useMainHospital';
import type { Hospital, HospitalSearchFilters, HospitalFormData } from './types';
import { normalizeHospitalsFromSearchResponse } from './hospitalSearchNormalize';
import { HospitalModal } from '@/components/organisms';
import {
  AppTable,
  AppTextField,
  AppButton,
  AppPagination,
  AppFormLabel,
  AppSpin,
  AppTableActionButton,
} from '@/components/atoms';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';

export interface HospitalMasterListPanelProps {
  variant: 'page' | 'modal';
  /** variant === 'modal': when false, skip modal reset and enter-key override */
  isActive?: boolean;
  initialHospitalName?: string;
  onPickHospital?: (hospital: Hospital) => void;
  onDeleteHospital?: (hospital: Hospital) => void;
}

function useListSearchParams(
  variant: 'page' | 'modal',
  isActive: boolean,
  initialHospitalName: string
): { searchParams: URLSearchParams; setSearchParams: SetURLSearchParams } {
  const [pageSearchParams, setPageSearchParams] = useSearchParams();
  const [modalParams, setModalParams] = useState(() => new URLSearchParams());

  useEffect(() => {
    if (variant !== 'modal' || !isActive) return;
    const next = new URLSearchParams();
    next.set('page', '1');
    next.set('sort_dir', 'asc');
    const hn = initialHospitalName.trim();
    if (hn) next.set('hospital_name', hn);
    setModalParams(next);
  }, [variant, isActive, initialHospitalName]);

  const setModalSearchParams = useCallback<SetURLSearchParams>((nextInit, navigateOpts) => {
    setModalParams(prev => {
      const resolved = typeof nextInit === 'function' ? nextInit(prev) : nextInit;
      return new URLSearchParams(resolved as string | string[][] | Record<string, string>);
    });
    void navigateOpts;
  }, []);

  if (variant === 'page') {
    return { searchParams: pageSearchParams, setSearchParams: setPageSearchParams };
  }
  return { searchParams: modalParams, setSearchParams: setModalSearchParams };
}

const HospitalMasterListPanel: React.FC<HospitalMasterListPanelProps> = ({
  variant,
  isActive = true,
  initialHospitalName = '',
  onPickHospital,
  onDeleteHospital,
}) => {
  const toast = useAppToast();
  const queryClient = useQueryClient();
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();
  const { searchParams, setSearchParams } = useListSearchParams(
    variant,
    isActive,
    initialHospitalName
  );

  const [searchHospitalName, setSearchHospitalName] = useState(
    () => searchParams.get('hospital_name') || ''
  );
  const [searchDoctorName, setSearchDoctorName] = useState(
    () => searchParams.get('doctor_name') || ''
  );

  useEffect(() => {
    setSearchHospitalName(searchParams.get('hospital_name') || '');
    setSearchDoctorName(searchParams.get('doctor_name') || '');
  }, [searchParams]);

  useEffect(() => {
    const enableOverride = variant === 'page' || (variant === 'modal' && isActive);
    if (!enableOverride) return;
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [variant, isActive, setEnterKeyNavigationEnabled]);

  const filters = useMemo<HospitalSearchFilters>(
    () => ({
      page_num: 20,
      sort_type: 'created_at',
      sort_dir: (searchParams.get('sort_dir') as 'asc' | 'desc') || 'asc',
      page: Number(searchParams.get('page')) || 1,
      hospital_name: searchParams.get('hospital_name') || undefined,
      doctor_name: searchParams.get('doctor_name') || undefined,
    }),
    [searchParams]
  );

  /** Track whether the user has triggered a search (modal variant only) */
  const [hasSearched, setHasSearched] = useState(
    () => variant === 'modal' && !!initialHospitalName.trim()
  );

  const [displayedHospitals, setDisplayedHospitals] = useState<Hospital[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit');
  const [editingHospitalId, setEditingHospitalId] = useState<number | null>(null);

  const { confirm } = useAppModal();
  const { mutateAsync: exportCsvMutation, isPending: exportCsvPending } =
    useMainHospitalExportCsv();

  const {
    data: searchResponse,
    isLoading,
    isFetching,
    isError: isSearchError,
    error: searchError,
  } = useMainHospitalSearch(filters);

  const {
    data: editDetailResponse,
    error: editDetailError,
    isLoading: editDetailLoading,
    isError: isEditDetailError,
  } = useMainHospitalDetail(editingHospitalId || 0);

  const createMutation = useMainHospitalCreate();
  const updateMutation = useMainHospitalUpdate();
  const deleteMutation = useMainHospitalDelete();

  const { hospitals, pagination } = useMemo(
    () => normalizeHospitalsFromSearchResponse(searchResponse),
    [searchResponse]
  );

  const editingHospital = editDetailResponse?.data || null;

  useEffect(() => {
    if (!isLoading) {
      setDisplayedHospitals(hospitals);
    }
  }, [hospitals, isLoading]);

  const isInitialLoading = isLoading && displayedHospitals.length === 0;
  const isPageLoading = !isInitialLoading && isFetching;

  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: isSearchError,
    dataLength: hospitals.length,
  });

  useEffect(() => {
    if (searchError) {
      toast.error('データの取得に失敗しました。');
    }
  }, [searchError, toast]);

  useEffect(() => {
    if (
      modalMode === 'edit' &&
      editingHospitalId &&
      editDetailResponse &&
      !editDetailLoading &&
      !editDetailError
    ) {
      setModalOpen(true);
    }
  }, [editDetailResponse, editDetailLoading, editDetailError, editingHospitalId, modalMode]);

  useEffect(() => {
    if (isEditDetailError && editDetailError && editingHospitalId && modalMode === 'edit') {
      if (!isNotFoundError(editDetailError)) {
        toast.error('医療機関の取得に失敗しました。');
      }
      setModalOpen(false);
      setEditingHospitalId(null);
    }
  }, [isEditDetailError, editDetailError, editingHospitalId, modalMode, toast]);

  const handlePageChange = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
  };

  /** Match HospitalPage: search resets URL to only page + name filters */
  const handleSearch = () => {
    if (variant === 'modal') setHasSearched(true);
    setSearchParams(
      new URLSearchParams({
        page: '1',
        hospital_name: searchHospitalName || '',
        doctor_name: searchDoctorName || '',
      })
    );
  };

  const handleExportCsv = async () => {
    try {
      await exportCsvMutation(filters);
    } catch (e: unknown) {
      const isOffline = typeof window !== 'undefined' && !window.navigator.onLine;
      const err = e as { code?: string; message?: string };
      const isAxiosNetworkError = err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';
      if (!isOffline && !isAxiosNetworkError) {
        toast.error('CSV出力に失敗しました。');
      }
    }
  };

  const handleNewClick = () => {
    setEditingHospitalId(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEditClick = useCallback((hospital: Hospital) => {
    setEditingHospitalId(hospital.id);
    setModalMode('edit');
  }, []);

  const invalidateListQueries = () => {
    queryClient.invalidateQueries({
      predicate: query => query.queryKey[0] === 'hospitals' && !query.queryKey.includes('detail'),
    });
  };

  const handleSave = async (data: HospitalFormData) => {
    // No try/catch: the modal's own submit handler reports the failure, and a
    // catch that only rethrows changes nothing but the stack it is read from.
    if (modalMode === 'create') {
      const response = await createMutation.mutateAsync(data);
      if (!response?.success) {
        throw new Error(response?.message || '登録に失敗しました。');
      }
      toast.success(response?.message || '医療機関を登録しました。');
      setModalOpen(false);
      setEditingHospitalId(null);
      invalidateListQueries();
    } else if (editingHospital) {
      await updateMutation.mutateAsync({ id: editingHospital.id, data });
      toast.success('更新が正常に終了しました。');
      setModalOpen(false);
      setEditingHospitalId(null);
    }
  };

  const handleDeleteConfirm = useCallback(
    async (hospitalToDelete: Hospital) => {
      if (!hospitalToDelete) return;
      try {
        const response = await deleteMutation.mutateAsync(hospitalToDelete.id);
        if (!response?.success) {
          throw new Error(response?.message || '削除に失敗しました。');
        }
        toast.success(response?.message || '削除が正常に終了しました。');
        onDeleteHospital?.(hospitalToDelete);
      } catch (err: unknown) {
        throw new Error(getApiErrorMessage(err, '削除に失敗しました。'));
      }
    },
    [deleteMutation, toast, onDeleteHospital]
  );

  const hospitalTableColumns = useMemo(
    () => [
      {
        title: '医療機関番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        align: 'left' as const,
      },
      {
        title: '医療機関名',
        dataIndex: 'name',
        key: 'name',
        align: 'left' as const,
        width: '18.8%',
        render: (_: unknown, record: Hospital) =>
          renderTableCellTruncate(record.name ?? '', {
            className: 'break-all',
          }),
      },
      {
        title: '医師名',
        width: '30%',
        key: 'doctors',
        align: 'left' as const,
        render: (_: unknown, record: Hospital) =>
          renderTableCellTruncate(record.doctors?.map(d => d.name).join('、') ?? '', {
            className: 'break-all',
          }),
      },
      {
        title: '住所',
        key: 'address',
        align: 'left' as const,
        width: '30%',
        render: (_: unknown, record: Hospital) =>
          renderTableCellTruncate((record.prefecture?.name ?? '') + (record.address ?? '')),
      },
      {
        title: '電話番号',
        dataIndex: 'telephone',
        key: 'telephone',
        align: 'left' as const,
      },
      {
        title: '操作',
        key: 'actions',
        align: 'center' as const,
        width: 170,
        render: (_: unknown, record: Hospital) => (
          <div className="flex justify-center gap-2 w-[170px]">
            <AppTableActionButton
              action="edit"
              onClick={e => {
                e.stopPropagation();
                handleEditClick(record);
              }}
              disabled={editingHospitalId === record.id && editDetailLoading}
              title="編集"
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              onClick={e => {
                e.stopPropagation();
                confirm({
                  title: '削除確認',
                  content: (
                    <div>
                      <p>この医療機関を削除してもよろしいですか？</p>
                    </div>
                  ),
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    try {
                      await handleDeleteConfirm(record);
                    } catch (err: unknown) {
                      const e = err as { message?: string };
                      toast.error(e?.message || '削除に失敗しました。');
                      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
                    }
                  },
                  onCancel: () => {},
                  okText: '削除',
                });
              }}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [
      confirm,
      editingHospitalId,
      editDetailLoading,
      handleEditClick,
      handleDeleteConfirm,
      toast,
      queryClient,
    ]
  );

  if (isInitialLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80 min-h-[240px]"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        {isPageLoading && (
          <div className="absolute inset-0 z-10 bg-white/60" aria-busy="true">
            <div
              className={
                variant === 'modal'
                  ? 'sticky top-0 flex items-center justify-center h-[50vh]'
                  : 'flex items-center justify-center h-full'
              }
            >
              <AppSpin size="large" />
            </div>
          </div>
        )}
        <div className="p-3 mb-4 rounded ">
          <div className="flex flex-wrap items-center gap-4 mb-3">
            <div className="flex items-center gap-2">
              <AppFormLabel label="医療機関名:" className="w-auto" />
              <AppTextField
                value={searchHospitalName}
                onChange={e => setSearchHospitalName(e.target.value)}
                onPressEnter={handleSearch}
                className="w-48 px-3 border rounded"
                placeholder="入力してください"
              />
            </div>
            <div className="flex items-center gap-2">
              <AppFormLabel label="医師名:" className="w-auto" />
              <AppTextField
                value={searchDoctorName}
                onChange={e => setSearchDoctorName(e.target.value)}
                onPressEnter={handleSearch}
                className="w-48 px-3 border rounded"
                placeholder="入力してください"
              />
            </div>
            <AppButton onClick={handleSearch} type="primary" icon={<i className="fa fa-search" />}>
              検索
            </AppButton>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <AppButton onClick={handleNewClick} type="primary" icon={<i className="fa fa-plus" />}>
            新規登録
          </AppButton>
          <AppButton type="primary" onClick={handleExportCsv} loading={exportCsvPending}>
            CSV出力
          </AppButton>
        </div>

        <AppTable
          dataSource={displayedHospitals}
          rowKey="id"
          pagination={false}
          bordered
          recordCount={pagination?.total ?? displayedHospitals.length}
          columns={hospitalTableColumns}
          hasFilters={hasSearched}
          onRow={
            onPickHospital
              ? record => ({
                  onClick: () => onPickHospital(record as Hospital),
                  className: 'cursor-pointer hover:bg-sky-50 transition-colors',
                })
              : undefined
          }
        />

        {pagination && (
          <div className="flex justify-center mt-4">
            <AppPagination
              current={pagination.current_page}
              total={pagination.total}
              pageSize={pagination.per_page || 20}
              onChange={handlePageChange}
              hideOnSinglePage
            />
          </div>
        )}
      </div>

      <HospitalModal
        hospital={editingHospital}
        isOpen={modalOpen}
        isEdit={true}
        onClose={() => {
          setModalOpen(false);
          setEditingHospitalId(null);
          invalidateListQueries();
        }}
        onSave={handleSave}
        isNew={modalMode === 'create'}
        skipBlocker={variant === 'modal'}
        enableBackClose={variant === 'modal'}
      />
    </>
  );
};

export default HospitalMasterListPanel;
